import { ReadWrite }	from "ts-base/type";
import { Equal }		from "ts-base/equal";
import { Either }		from "ts-base/either";

export type Json = Json.Scalar|Json.Array|Json.Object;

export namespace Json {
	//-----------------------------------------------------------------------------
	//## variants

	// TODO json have a branded type for numbers without NaN and Infinity?
	export type Scalar	= number|string|boolean|null;

	export type Array	= ReadonlyArray<Json>;

	// TODO json should we use { [ key in string ]?: Json }; for objects?
	// eslint-disable-next-line @typescript-eslint/consistent-indexed-object-style
	export type Object	= Readonly<{ [k:string]: Json }>;

	export const isScalar	= (it:Json):it is Json.Scalar	=> it === null || typeof it !== "object";
	export const isArray	= (it:Json):it is Json.Array	=> Array.isArray(it);
	export const isObject	= (it:Json):it is Json.Object	=> it !== null && typeof it === "object" && !Array.isArray(it);

	//-----------------------------------------------------------------------------
	//## parsing and unparsing

	/** catches the SyntaxError thrown on invalid json */
	export const parse	= (it:string):Either<SyntaxError, Json>	=> {
		try {
			return Either.right(JSON.parse(it));
		}
		catch (e) {
			if (e instanceof SyntaxError)	return Either.left(e);
			// this does not happen
			throw e;
		}
	};

	/** throws a SyntaxError when invalid */
	export const unsafeParse	= (it:string):Json	=>
		JSON.parse(it);

	/** replaces JSON.stringify which has an overbroad "any" type for its parameter */
	export const stringify	= (it:Json, space?: string | number):string	=>
		JSON.stringify(it, null, space);

	//-----------------------------------------------------------------------------
	//## comparison and serialization

	export const deepEqual:Equal<Json>	= (a, b):boolean	=> {
		if (a === b)	return true;

		// NOTE typeof null === "object"
		if (a === null || b === null)						return false;
		if (typeof a !== "object" || typeof b !== "object")	return false;

		if (Array.isArray(a) && Array.isArray(b)) {
			const aa	= a as Json.Array;
			const bb	= b as Json.Array;
			if (aa.length !== bb.length)	return false;

			for (let i=0; i<aa.length; i++) {
				if (!deepEqual(aa[i], bb[i]))	return false;
			}
			return true;
		}
		else if (!Array.isArray(a) && !Array.isArray(b)) {
			const ka	= Object.keys(a);
			const kb	= Object.keys(b);
			if (ka.length !== kb.length)	return false;

			const aa	= a as Json.Object;
			const bb	= b as Json.Object;
			for (const k of ka) {
				// TODO json use Object.hasOwn here when it becomes available
				if (!Object.prototype.hasOwnProperty.call(b, k))	return false;
				if (!deepEqual(aa[k], bb[k]))						return false;
			}
			return true;
		}

		return false;
	};

	export const sortKeys	= (json:Json):Json	=> {
		if (isArray(json)) {
			return json.map(sortKeys);
		}
		else if (isObject(json)) {
			const out	= {} as ReadWrite<Json.Object>;
			const keys	= Object.keys(json);
			keys.sort();
			for (const key of keys) {
				out[key]	= sortKeys(json[key]);
			}
			return out;
		}
		else return json;
	};
}
